import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Layout from "../../components/global/layout";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import { toast } from "react-toastify";
import ActionMenu from "../../components/common/ActionMenu";
import CommonPagination from "../../components/common/CommonPagination";
import FormControl from "../../components/common/FormControl";
import { MODULE_CONFIG, formatCurrency, formatDate, formatTime, capitalize } from "./shared";
import {
  FiSearch,
  FiEye,
  FiRotateCcw,
  FiX,
  FiCopy,
} from "react-icons/fi";
import { MdOutlinePayment } from "react-icons/md";
import { HiOutlineBuildingOffice2 } from "react-icons/hi2";

export default function TransactionsList() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);

  // State
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Filters & Pagination (Default 25 per page)
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedTenant, setSelectedTenant] = useState("");

  // Handle immediate search input clearing
  const handleSearchChange = (val) => {
    setSearchQuery(val);
    if (!val || !val.trim()) {
      loader(true);
      setDebouncedSearch("");
      setPage(1);
    }
  };

  // Debounce search input (350ms)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setDebouncedSearch("");
      return;
    }
    const handler = setTimeout(() => {
      loader(true);
      setDebouncedSearch(searchQuery.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Fetch transactions from API
  const fetchTransactions = useCallback(
    async (showLoader = true) => {
      if (showLoader) loader(true);
      setLoading(true);

      try {
        const queryParams = new URLSearchParams();
        queryParams.append("page", String(page));
        queryParams.append("limit", String(limit || 25));
        queryParams.append("count", String(limit || 25));

        // Append tenantId if selected
        if (selectedTenant && selectedTenant.trim()) {
          queryParams.append("tenantId", selectedTenant.trim());
        }

        // Handle transactionStatus filter
        if (statusFilter && statusFilter !== "all") {
          queryParams.append("transactionStatus", statusFilter);
        }

        if (debouncedSearch && debouncedSearch.trim()) {
          queryParams.append("search", debouncedSearch.trim());
        }

        const endpoint = `${MODULE_CONFIG.endpoints.listing}?${queryParams.toString()}`;
        const res = await ApiClient.get(endpoint);

        if (res?.success) {
          const data = res?.data;
          const allRecords = Array.isArray(data?.transactions)
            ? data.transactions
            : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(data?.docs)
            ? data.docs
            : Array.isArray(data?.list)
            ? data.list
            : Array.isArray(data)
            ? data
            : [];

          const dynamicTotal =
            Number(
              res?.total ??
              res?.totalDocs ??
              res?.count ??
              data?.total ??
              data?.totalDocs ??
              data?.count ??
              allRecords.length
            ) || 0;

          setTotalCount(dynamicTotal);

          // If server did not slice records according to limit, paginate on client
          const paginatedRecords =
            allRecords.length > (limit || 25)
              ? allRecords.slice((page - 1) * (limit || 25), page * (limit || 25))
              : allRecords;

          setItems(paginatedRecords);
        } else {
          setItems([]);
          setTotalCount(0);
        }
      } catch (err) {
        console.error("Failed to fetch transactions:", err);
        setItems([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
        if (showLoader) loader(false);
      }
    },
    [page, limit, statusFilter, debouncedSearch, selectedTenant]
  );

  useEffect(() => {
    fetchTransactions(true);
  }, [fetchTransactions]);

  // Reset filters
  const isFilterActive = Boolean(
    searchQuery.trim() ||
      (statusFilter && statusFilter !== "all") ||
      (selectedTenant && selectedTenant.trim())
  );

  const handleResetFilters = () => {
    loader(true);
    setSearchQuery("");
    setDebouncedSearch("");
    setStatusFilter("all");
    setSelectedTenant("");
    setPage(1);
  };

  // Helper to copy text to clipboard
  const handleCopy = (text, label = "ID") => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.info(`${label} copied to clipboard!`);
  };

  // Helper to extract status badge
  const getStatusBadge = (statusKey) => {
    const key = (statusKey || "pending").toLowerCase();
    const config = MODULE_CONFIG.statusMap[key] || {
      label: capitalize(key),
      bgClass: "bg-slate-200 text-slate-900 border-slate-300 font-bold",
      dotClass: "bg-slate-600",
    };
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] uppercase tracking-wide border whitespace-nowrap shadow-2xs ${config.bgClass}`}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${config.dotClass}`} />
        {config.label}
      </span>
    );
  };

  return (
    <Layout>
      <div className="w-full max-w-full space-y-5 pb-12 antialiased">
        {/* ======================================================== */}
        {/* 1. TOP HEADER & FILTER BAR */}
        {/* ======================================================== */}
        <div className="bg-white rounded-2xl p-4 sm:p-5 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.03)] flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="space-y-0.5 min-w-0">
            <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight truncate">
              {MODULE_CONFIG.title}
            </h1>
            <p className="text-xs text-slate-700 font-semibold">
              {MODULE_CONFIG.subtitle}
            </p>
          </div>

          {/* Filter & Action Controls */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 flex-wrap shrink-0">
            {/* Search Input */}
            <div className="w-full sm:w-64 md:w-72">
              <div className="relative flex items-center group">
                <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-500 group-focus-within:text-[#115B9D] transition-colors">
                  <FiSearch className="w-4 h-4" />
                </span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search order ID, customer, tenant..."
                  className="w-full bg-white border-[1.5px] border-slate-500 rounded-xl pl-10 pr-9 h-10 text-xs sm:text-sm font-semibold text-slate-900 placeholder:text-slate-500 outline-none focus:outline-none focus:ring-0 focus:border-slate-500 shadow-box"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => handleSearchChange("")}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
                    title="Clear search"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Status Filter Dropdown */}
            <div className="w-full sm:w-48 shrink-0">
              <FormControl
                type="select"
                placeholder="Filter by Status"
                value={statusFilter}
                theme="search"
                options={MODULE_CONFIG.statusOptions}
                onChange={(val) => {
                  loader(true);
                  setStatusFilter(val || "all");
                  setPage(1);
                }}
              />
            </div>

            {/* Reset Filters Button */}
            <button
              type="button"
              onClick={handleResetFilters}
              disabled={!isFilterActive}
              className={`inline-flex items-center justify-center gap-1.5 px-3.5 h-10 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                isFilterActive
                  ? "bg-white hover:bg-slate-100 active:bg-slate-200 text-slate-800 hover:text-slate-950 border border-slate-300 hover:border-slate-400 shadow-xs"
                  : "bg-slate-50 text-slate-400 border border-slate-200 opacity-50 cursor-not-allowed"
              }`}
              title="Reset all search and status filters"
            >
              <FiRotateCcw className={`w-3.5 h-3.5 ${isFilterActive ? "text-slate-700" : "text-slate-400"}`} />
              <span>Reset</span>
            </button>
          </div>
        </div>

        {/* ======================================================== */}
        {/* 2. TRANSACTIONS LISTING (STICKY HEADER DESKTOP TABLE)    */}
        {/* ======================================================== */}
        <div className="bg-transparent md:bg-white md:rounded-2xl md:border md:border-slate-300 md:shadow-[0_2px_10px_rgba(0,0,0,0.03)] overflow-visible">
          {/* Loading Indicator */}
          {loading && items.length === 0 ? (
            <div className="py-20 text-center bg-white rounded-2xl border border-slate-300 md:border-0">
              <div className="w-8 h-8 border-3 border-[#115B9D] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-xs font-bold text-slate-900">Loading Transactions...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center space-y-3 p-6 bg-white rounded-2xl border border-slate-300 md:border-0">
              <div className="w-12 h-12 rounded-2xl bg-slate-100 border border-slate-300 text-slate-600 flex items-center justify-center mx-auto mb-2">
                <MdOutlinePayment className="w-6 h-6" />
              </div>
              <h3 className="text-sm font-black text-slate-900">No Transactions Found</h3>
              <p className="text-xs font-semibold text-slate-700 max-w-md mx-auto">
                {debouncedSearch || statusFilter !== "all" || selectedTenant
                  ? "No transactions match your current search or status filter. Try resetting filters."
                  : "No transactions have been recorded yet."}
              </p>
              {isFilterActive && (
                <div className="pt-2 flex items-center justify-center">
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold transition-all cursor-pointer border border-slate-300"
                  >
                    <FiRotateCcw className="w-3.5 h-3.5" />
                    <span>Reset Filters</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* DESKTOP TABLE VIEW WITH FIXED STICKY HEADER */}
              <div className="hidden md:block w-full">
                <table className="w-full table-fixed border-collapse text-left text-xs">
                  <colgroup>
                    <col style={{ width: "5%" }} />   {/* 1. Action */}
                    <col style={{ width: "14%" }} />  {/* 2. Order ID */}
                    <col style={{ width: "16%" }} />  {/* 3. Tenant */}
                    <col style={{ width: "15%" }} />  {/* 4. Customer */}
                    <col style={{ width: "11%" }} />  {/* 5. Amount */}
                    <col style={{ width: "9%" }} />   {/* 6. Method */}
                    <col style={{ width: "12%" }} />  {/* 7. Status */}
                    <col style={{ width: "18%" }} />  {/* 8. Date */}
                  </colgroup>

                  <thead className="sticky top-[72px] z-30 bg-slate-100 shadow-xs">
                    <tr className="border-b border-slate-300 text-[11px] font-black uppercase tracking-wider text-slate-800 bg-slate-100">
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-3 text-center whitespace-nowrap border-b border-slate-300 shadow-xs rounded-tl-2xl">
                        Action
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-left border-b border-slate-300 shadow-xs">
                        Transaction / Order
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-left border-b border-slate-300 shadow-xs">
                        Tenant
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-left border-b border-slate-300 shadow-xs">
                        Customer
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-right border-b border-slate-300 shadow-xs">
                        Amount
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-center border-b border-slate-300 shadow-xs">
                        Method
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-center border-b border-slate-300 shadow-xs">
                        Status
                      </th>
                      <th className="sticky top-[72px] z-30 bg-slate-100 py-3.5 px-4 text-left whitespace-nowrap border-b border-slate-300 shadow-xs rounded-tr-2xl">
                        Date
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-200">
                    {items.map((item, index) => {
                      const id = item?.id || item?._id;
                      const orderRef =
                        item?.orderId ||
                        item?.transactionId ||
                        item?.stripePaymentIntentId ||
                        id?.slice(-8);
                      const customerName =
                        item?.customerName ||
                        item?.name ||
                        item?.customer?.name ||
                        "—";
                      const customerEmail =
                        item?.customerEmail ||
                        item?.email ||
                        item?.customer?.email ||
                        "";
                      const tenantName =
                        item?.tenantName ||
                        item?.tenantId?.name ||
                        item?.tenantId?.companyName ||
                        "Direct Platform";
                      const amount = item?.amount ?? 0;
                      const currency = item?.currency || "USD";
                      const paymentMethod =
                        item?.paymentMethod ||
                        item?.payment_method ||
                        item?.method ||
                        "CARD";
                      const status =
                        item?.transactionStatus ||
                        item?.status ||
                        "completed";
                      const createdAt = item?.createdAt || item?.date;

                      return (
                        <tr
                          key={id || `tx-${index}`}
                          className="hover:bg-slate-50/90 transition-colors group"
                        >
                          {/* 1. Action Menu (Column 1) */}
                          <td className="py-3.5 px-3 text-center">
                            <ActionMenu
                              align="left"
                              actions={[
                                {
                                  label: "View Dossier",
                                  icon: <FiEye className="w-4 h-4 text-slate-800" />,
                                  onClick: () =>
                                    navigate(
                                      `${MODULE_CONFIG.routeBase}/detail/${id}`
                                    ),
                                },
                                {
                                  label: "Copy Order ID",
                                  icon: <FiCopy className="w-4 h-4 text-slate-800" />,
                                  onClick: () => handleCopy(orderRef, "Order ID"),
                                },
                              ]}
                            />
                          </td>

                          {/* 2. Transaction / Order Reference */}
                          <td className="py-3.5 px-4">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-black text-xs text-slate-950 bg-slate-100 px-2 py-0.5 rounded border border-slate-300">
                                #{orderRef}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleCopy(orderRef, "Order ID")}
                                className="text-slate-500 hover:text-slate-800 transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
                                title="Copy Order ID"
                              >
                                <FiCopy className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            {item?.stripePaymentIntentId && (
                              <div className="text-[10px] font-mono text-slate-600 truncate max-w-[180px] mt-0.5">
                                {item.stripePaymentIntentId}
                              </div>
                            )}
                          </td>

                          {/* 3. Tenant */}
                          <td className="py-3.5 px-4">
                            <div className="flex items-center gap-1.5 font-bold text-xs text-slate-900">
                              <HiOutlineBuildingOffice2 className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                              <span className="truncate max-w-[160px]">{tenantName}</span>
                            </div>
                          </td>

                          {/* 4. Customer */}
                          <td className="py-3.5 px-4">
                            <div className="font-bold text-xs text-slate-900 truncate max-w-[180px]">
                              {customerName}
                            </div>
                            {customerEmail && (
                              <div className="text-[11px] font-medium text-slate-700 truncate max-w-[180px]">
                                {customerEmail}
                              </div>
                            )}
                          </td>

                          {/* 5. Amount */}
                          <td className="py-3.5 px-4 text-right">
                            <div className="font-black text-xs sm:text-sm text-slate-950">
                              {formatCurrency(amount, currency)}
                            </div>
                            <span className="text-[10px] font-bold text-slate-600 uppercase">
                              {currency}
                            </span>
                          </td>

                          {/* 6. Payment Method */}
                          <td className="py-3.5 px-4 text-center">
                            <span className="uppercase font-bold text-[10px] tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-800 border border-slate-300 whitespace-nowrap">
                              {paymentMethod}
                            </span>
                          </td>

                          {/* 7. Status */}
                          <td className="py-3.5 px-4 text-center">
                            {getStatusBadge(status)}
                          </td>

                          {/* 8. Date */}
                          <td className="py-3.5 px-4 text-left whitespace-nowrap">
                            <div className="text-xs font-bold text-slate-900">
                              {createdAt ? formatDate(createdAt) : "—"}
                            </div>
                            {createdAt && (
                              <div className="text-[11px] font-medium text-slate-600">
                                {formatTime(createdAt)}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* MOBILE CARDS VIEW (NO OUTER WHITE BOX WRAPPING) */}
              <div className="block md:hidden space-y-3.5">
                {items.map((item, index) => {
                  const id = item?.id || item?._id;
                  const orderRef =
                    item?.orderId ||
                    item?.transactionId ||
                    item?.stripePaymentIntentId ||
                    id?.slice(-8);
                  const customerName =
                    item?.customerName ||
                    item?.name ||
                    item?.customer?.name ||
                    "—";
                  const customerEmail =
                    item?.customerEmail ||
                    item?.email ||
                    item?.customer?.email ||
                    "";
                  const tenantName =
                    item?.tenantName ||
                    item?.tenantId?.name ||
                    item?.tenantId?.companyName ||
                    "Direct Platform";
                  const amount = item?.amount ?? 0;
                  const currency = item?.currency || "USD";
                  const paymentMethod =
                    item?.paymentMethod ||
                    item?.payment_method ||
                    item?.method ||
                    "CARD";
                  const status =
                    item?.transactionStatus ||
                    item?.status ||
                    "completed";
                  const createdAt = item?.createdAt || item?.date;

                  return (
                    <div
                      key={id || `card-${index}`}
                      className="bg-white rounded-2xl p-4 border border-slate-300 shadow-[0_2px_10px_rgba(0,0,0,0.03)] space-y-3"
                    >
                      {/* Top Header: Order ID & ActionMenu */}
                      <div className="flex items-center justify-between gap-2 border-b border-slate-200 pb-2.5">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-black text-xs text-slate-950 bg-slate-100 px-2 py-0.5 rounded border border-slate-300">
                            #{orderRef}
                          </span>
                          <span className="uppercase font-bold text-[10px] tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-800 border border-slate-300">
                            {paymentMethod}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusBadge(status)}
                          <ActionMenu
                            align="right"
                            actions={[
                              {
                                label: "View Dossier",
                                icon: <FiEye className="w-4 h-4 text-slate-800" />,
                                onClick: () =>
                                  navigate(
                                    `${MODULE_CONFIG.routeBase}/detail/${id}`
                                  ),
                              },
                              {
                                label: "Copy Order ID",
                                icon: <FiCopy className="w-4 h-4 text-slate-800" />,
                                onClick: () => handleCopy(orderRef, "Order ID"),
                              },
                            ]}
                          />
                        </div>
                      </div>

                      {/* Middle: Customer & Tenant */}
                      <div className="space-y-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-medium text-slate-600">Customer:</span>
                          <span className="font-bold text-slate-950">{customerName}</span>
                        </div>
                        {customerEmail && (
                          <div className="flex items-center justify-between text-[11px]">
                            <span className="font-medium text-slate-600">Email:</span>
                            <span className="font-semibold text-slate-800">{customerEmail}</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between text-xs">
                          <span className="font-medium text-slate-600">Tenant:</span>
                          <span className="font-bold text-slate-900">{tenantName}</span>
                        </div>
                      </div>

                      {/* Bottom: Amount & Date */}
                      <div className="flex items-center justify-between pt-2.5 border-t border-slate-200 text-xs">
                        <div>
                          <span className="text-[10px] font-bold text-slate-600 uppercase block">
                            Amount Charged
                          </span>
                          <span className="font-black text-base text-slate-950">
                            {formatCurrency(amount, currency)}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="text-[10px] font-bold text-slate-600 uppercase block">
                            Date
                          </span>
                          <span className="font-bold text-slate-900">
                            {createdAt ? formatDate(createdAt) : "—"}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* PAGINATION CONTROLS (Only visible when total items exceed page limit) */}
              {totalCount > limit && (
                <div className="p-4 bg-white border-t border-slate-200 rounded-b-2xl">
                  <CommonPagination
                    page={page}
                    limit={limit}
                    total={totalCount}
                    onPageChange={(p) => {
                      loader(true);
                      setPage(p);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    onLimitChange={(l) => {
                      loader(true);
                      setLimit(l);
                      setPage(1);
                    }}
                    limitOptions={[25, 50, 100]}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
